/** @jsx jsx */
import { Box, Button, Columns, jsx, Tiles } from '@front10/base-elements';
import { useTranslation } from '@front10/utils';
import { useEffect, useState } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import type { FieldError } from 'react-hook-form';
import { Combobox } from '../../lab/Combobox';
import { DateRangePicker, Dates } from '../../lab/DateRangePicker';
import { Disclaimer } from '../../lab/Disclaimer';
import { DisclaimerLink } from '../../lab/DisclaimerLink';
import { ErrorMessage } from '../../lab/ErrorMessage';
import { FetchAirlines, useAirlines } from './airlines';
import { Airport, AirportMarketFilter, FetchAirports, useAirports } from './airports';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { OriginDestinationLogic } from '../_commons/filters';
import React from 'react';
import { BlackoutDates } from './blackoutDates';
import { isNilOrEmpty } from '../../../helpers/utils';
import { Spinner } from '@theme-ui/components';

export type BookingFilterLabels = Partial<
  Record<
    | 'submit'
    | 'origin'
    | 'destination'
    | 'return'
    | 'departure'
    | 'legend'
    | 'originPlaceholder'
    | 'destinationPlaceholder'
    | 'departurePlaceholder'
    | 'returnPlaceholder'
    | 'startDateValidationMessage'
    | 'startTimeValidationMessage'
    | 'endDateValidationMessage'
    | 'endTimeValidationMessage'
    | 'rangeDateValidationMessage'
    | 'rangeDateTimeValidationMessage',
    'string'
  >
>;

const ENTER_KEY = 13;

interface Props {
  defaultOrigin: Airport;
  prepopulatedAirlines?: string;
  selectedDepartureDate: Date | undefined;
  selectedReturnDate: Date | undefined;
  defaultDestination: Airport;
  defaultDates?: Dates;
  fetchAirports: FetchAirports;
  fetchAirlines: FetchAirlines;
  filterFields?: AirportMarketFilter;
  showOrigin?: boolean;
  showDestination?: boolean;
  showTimePickers?: boolean;
  onOriginChange: (origin: Airport) => void;
  onAirlineChange: (airline: { value: string; label: string }) => void;
  onDestinationChange: (destination: Airport) => void;
  onDepartureDateChange: (departureDate: Date | undefined, returnDate: Date | undefined) => void;
  onReturnDateChange: (returnDate: Date | undefined, departureDate: Date | undefined) => void;
  labels: BookingFilterLabels;
  componentsConfigurations: Pick<
    BookingComponentsConfigurations,
    'locationSelector' | 'dateSelector' | 'flexibleDatesSelector' | 'airlinesSelector'
  >;
  blackoutDates?: {
    minDate: number;
    maxDate: number;
  };
  maxTripDuration?: number;
  minTripDuration?: number;
  updateSelectedOrigin: (selectedOrigin: string) => void;
  updateSelectedDestination: (selectedDestination: string) => void;
  updateSelectedDepartureDate: (departureDate: Date | undefined) => void;
  updateSelectedReturnDate: (departureDate: Date | undefined) => void;
  columns: number[][];
  rows?: string[];
  ctaHalfWidth?: boolean;
  showAirlines?: boolean;
  airlines?: {
    airlinesList: Array<{}>;
    enabled: boolean;
  };
  singleDateSelection: boolean;
  activeTab?: number;
  currentTab?: number;
  originDestinationLogic: string;
  trigger?: any;
  onSubmit?: any;
  blackoutDatesIntegration?: boolean;
  flightType?: string;
  fetchBlackoutDates?: BlackoutDates | null;
  setValue?: (key: string, value: any) => void;
  getValues?: () => any;
  disclaimer?: BookingComponentsConfigurations['disclaimer'];
  allowHoverStyleInRange?: boolean;
  timePickerOptions?: {
    options: Array<{ value: string; labelKey: string }>;
    defaultValue: string;
  };
  isLoading?: boolean;
}

export type AirlinesList = Array<{
  labelKey: string;
  defaultValue: string;
  airlineCode: string;
}>;
export type AirlinesConfiguration = {
  airlinesList: AirlinesList;
  enabled: boolean;
};

const timeOptions = ['10:30 AM', '2:00 PM', '5:00 PM'].map((v) => ({
  value: v,
  labelKey: v
}));

export type Airlines = {
  label: string;
  value: string;
};

export type DatePickerLabel = {
  departure: string;
  return: string;
};

export function BookingFilters(
  props: Props &
    Pick<
      UseFormMethods<{
        origin?: Airport;
        destination?: Airport;
        dates?: $TSFixMe;
        airlines?: Airlines;
      }>,
      'watch' | 'errors' | 'control'
    >
) {
  const translate = useTranslation();
  const errors = props.errors;
  const labels = props.labels;
  const selectedOrigin = props.watch('origin', props.defaultOrigin);
  const isOriginPrepopulated = props.defaultOrigin && props.defaultOrigin.value !== '';
  const [wasSetedPrepopulatedOriginValue, setWasSetedPrepopulatedOriginValue] = React.useState(
    false
  );
  const isDestinationPrepopulated =
    props.defaultDestination && props.defaultDestination.value !== '';
  const [
    wasSetedPrepopulatedDestinationValue,
    setWasSetedPrepopulatedDestinationValue
  ] = React.useState(false);
  const selectedAirlines = props.watch('airlines', {
    value: props.prepopulatedAirlines ?? '',
    label: props.prepopulatedAirlines ?? ''
  });
  const selectedDestination = props.watch('destination', props.defaultDestination);
  const [selectedDates, setSelectedDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null
  });

  const [resetDestinationValue, setResetDestinationValue] = useState(false);

  const locationSelector = props.componentsConfigurations.locationSelector ?? {};
  const originTitle = props.componentsConfigurations.locationSelector?.originTitle
    ? translate(props.componentsConfigurations.locationSelector?.originTitle)
    : '';
  const destinationTitle = props.componentsConfigurations.locationSelector?.destinationTitle
    ? translate(props.componentsConfigurations.locationSelector?.destinationTitle)
    : '';
  const datePicker = props.componentsConfigurations.dateSelector;
  const flexibleDates = props.componentsConfigurations.flexibleDatesSelector;
  const airline = props.componentsConfigurations.airlinesSelector;

  const filterFields = props.filterFields ?? AirportMarketFilter.NONE;

  const showOrigin = props.showOrigin ?? true;
  const showDestination = props.showDestination ?? true;

  const sameCall = props.originDestinationLogic === OriginDestinationLogic.SAME;

  const transformTimeOptions = React.useCallback(
    (opt: Array<{ value: string; labelKey: string }>) => {
      return opt.map((elem) => ({
        value: elem.value,
        label: translate(elem.labelKey, elem.labelKey)
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const timePickerOptions = React.useMemo(
    () => transformTimeOptions(props.timePickerOptions?.options ?? timeOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.timePickerOptions?.options]
  );

  const { data: origins = [] } = useAirports(props.fetchAirports, {
    airportType: 'ORIGIN',
    selectedDestination:
      selectedDestination?.value && !sameCall ? selectedDestination.value : undefined,
    filterFields,
    avoidCall: !showOrigin,
    sameCall: sameCall,
    panel: props.activeTab
  });

  let { data: destinations = [] } = useAirports(props.fetchAirports, {
    airportType: 'DESTINATION',
    selectedOrigin: selectedOrigin?.value ? selectedOrigin.value : undefined,
    filterFields,
    avoidCall: !showDestination || sameCall,
    panel: props.activeTab
  });

  if (sameCall && destinations.length === 0) {
    destinations = [...origins];
  }

  const { data: fetchAirlines = [] } = useAirlines(props.fetchAirlines, {
    selectedOrigin: props.airlines?.enabled ? selectedOrigin?.value : '',
    selectedDestination: props.airlines?.enabled ? selectedDestination?.value : '',
    enableFilter: props.showAirlines
  });

  function findIataCode(iataCode: string, airlines: any) {
    if (airlines) {
      for (let index = 0; index < airlines.length; index++) {
        const airline = airlines[index];
        if (airline.airlineCode === iataCode) {
          return airline;
        }
      }
    }
    return null;
  }

  function getAvailableAirlines(origin: string, destination: string, airlines: AirlinesList) {
    let newOptions = [];
    for (let index = 0; index < fetchAirlines.length; index++) {
      const element = fetchAirlines[index];
      if (element.valid) {
        const findElement = findIataCode(element.iataCode, airlines);
        if (findElement !== null) {
          newOptions.push({
            value: findElement.airlineCode,
            label: translate(findElement.labelKey, findElement.defaultValue)
          });
        }
      }
    }
    return newOptions;
  }

  function disabledArLineFilter() {
    return (
      !selectedOrigin ||
      !selectedDestination ||
      selectedOrigin?.value === '' ||
      selectedDestination?.value === ''
    );
  }

  function getOptions(airlines: any) {
    const origin = selectedOrigin?.value;
    const destination = selectedDestination?.value;
    const airlinesList = airlines.airlinesList;
    let newOptions: any[] = [];
    if (airlinesList !== undefined && airlinesList.length > 0) {
      if (origin === '' && destination === '') {
        for (let index = 0; index < airlinesList.length; index++) {
          const airline = airlinesList[index];
          newOptions.push({
            value: airline.airlineCode,
            label: translate(airline.labelKey, airline.defaultValue)
          });
        }
      } else if (origin === '' || destination === '') {
        return [];
      } else if (origin && origin !== '' && destination && destination !== '') {
        newOptions = getAvailableAirlines(origin, destination, airlinesList);
      }
    }
    return newOptions;
  }

  const airlinesOptions = props.airlines !== undefined ? getOptions(props.airlines) : [];

  useEffect(() => {
    props.updateSelectedOrigin(selectedOrigin?.value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrigin]);

  useEffect(() => {
    props.updateSelectedDestination(selectedDestination?.value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDestination]);

  const setTimeInDate = (date: Date, time: string) => {
    const TT = time?.toLowerCase() === 'noon' ? '12:00' : time?.toLowerCase();

    const [t, meridiem] = TT.split(' ');
    const [h, m] = t.split(':');

    date.setHours(meridiem?.toLowerCase() === 'pm' ? +h + 12 : +h);
    date.setMinutes(+m);
    date.setSeconds(0);

    return date;
  };

  const isValidDate = (dates: Dates) => {
    const initDate = setTimeInDate(new Date(dates.start ?? ''), dates.startTime ?? 'noon');
    const endDate = setTimeInDate(new Date(dates.end ?? ''), dates.endTime ?? 'noon');

    return endDate.valueOf() - initDate.valueOf() > 0;
  };

  const DateRangePickerRef = React.useRef<any>(null);
  return (
    <Columns variant="layout.filters" gap={2} columns={props.columns} rows={props.rows}>
      {showOrigin ? (
        <Controller
          render={({ onChange, name }) => (
            <Combobox
              key={props.activeTab}
              descriptionIconCloseAction={translate(
                'cmp-clean-button-action-description-origin',
                'Location cleared'
              )}
              isPrepopulatedValue={isOriginPrepopulated}
              wasSetedPrepopulatedValue={wasSetedPrepopulatedOriginValue}
              setWasSetedPrepopulatedValue={setWasSetedPrepopulatedOriginValue}
              defaultValue={origins.find((elem) => elem.value === props.defaultOrigin.value)?.value}
              label={translate(labels.origin || 'From')}
              options={origins}
              active={Boolean(selectedOrigin?.value)}
              onChange={(newValue) => {
                if (newValue.value && newValue.value !== selectedOrigin?.value) {
                  props.onOriginChange(newValue);
                }
                if (
                  newValue.label &&
                  newValue.label !== '' &&
                  newValue.label === selectedDestination?.label
                ) {
                  setResetDestinationValue(true);
                  setTimeout(() => setResetDestinationValue(false), 500);
                }
                onChange(newValue);
              }}
              errorMessage={<ErrorMessage errors={errors} name={name} />}
              hasError={!!errors['origin']}
              placeholder={translate(
                labels.originPlaceholder ?? 'cmp-booking-flight-origin-placeholder',
                'Choose city or airport'
              )}
              onKeyDown={(e) => {
                if (e.keyCode === ENTER_KEY) {
                  setTimeout(() => {
                    if (props?.trigger) {
                      props?.trigger('origin');
                    }
                  }, 200);
                }
              }}
              ariaLabelIconClose={translate('cmp-clear-icon', 'Clear')}
              ariaLabelIconToggle={translate('cmp-clear-icon-toggle', 'Clear')}
              {...locationSelector}
              title={originTitle}
            />
          )}
          name="origin"
          control={props.control}
          rules={{
            validate: {
              required: (origin) =>
                !!origin.label || translate('cmp-origin-validation-message', 'Origin is required')
            }
          }}
        />
      ) : null}
      {showDestination ? (
        <Controller
          render={({ onChange, name }) => (
            <Combobox
              key={props.activeTab}
              descriptionIconCloseAction={translate(
                'cmp-clean-button-action-description-destination',
                'Location cleared'
              )}
              isPrepopulatedValue={isDestinationPrepopulated}
              wasSetedPrepopulatedValue={wasSetedPrepopulatedDestinationValue}
              setWasSetedPrepopulatedValue={setWasSetedPrepopulatedDestinationValue}
              defaultValue={props.defaultDestination.value}
              hasError={!!errors['destination']}
              errorMessage={<ErrorMessage errors={errors} name={name} />}
              placeholder={translate(
                labels.destinationPlaceholder ?? 'cmp-booking-flight-destination-placeholder',
                'Choose city or airport'
              )}
              label={translate(labels.destination || 'To')}
              active={Boolean(selectedDestination?.value)}
              onChange={(newValue) => {
                if (newValue.value && newValue.value !== selectedDestination?.value) {
                  props.onDestinationChange(newValue);
                }
                onChange(newValue);
              }}
              options={destinations}
              ariaLabelIconClose={translate('cmp-clear-icon', 'Clear')}
              ariaLabelIconToggle={translate('cmp-clear-icon-toggle', 'Clear')}
              onKeyDown={(e) => {
                if (e.keyCode === ENTER_KEY) {
                  setTimeout(() => {
                    if (props?.trigger) {
                      props?.trigger('destination');
                    }
                  }, 200);
                }
              }}
              {...locationSelector}
              title={destinationTitle}
              resetInputValue={resetDestinationValue}
            />
          )}
          name="destination"
          control={props.control}
          rules={{
            validate: {
              required: (destination) =>
                !!destination.label ||
                translate('cmp-destination-validation-message', 'Destination is required')
            }
          }}
        />
      ) : null}

      <Controller
        render={({ onChange, onBlur, value, name }) => (
          <DateRangePicker
            ref={DateRangePickerRef}
            shouldFlip={datePicker?.shouldFlip !== undefined ? datePicker?.shouldFlip : false}
            activeTab={props.activeTab}
            currentTab={props.currentTab}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            labels={labels}
            startDateErrorMessage={
              <ErrorMessage
                errors={{
                  ...errors,
                  dates: {
                    ...errors.dates,
                    message:
                      errors.dates?.type === 'requiredDiffTime'
                        ? errors.dates?.message
                        : translate(
                            props.labels?.startDateValidationMessage ??
                              'cmp-date-range-start-validation-message',
                            'Pick a start date'
                          )
                  }
                }}
                name={name}
              />
            }
            startDateTimeErrorMessage={
              <ErrorMessage
                errors={{
                  ...errors,
                  dates: {
                    ...errors.dates,
                    message:
                      errors.dates?.type === 'requiredDiffTime'
                        ? errors.dates?.message
                        : translate(
                            props.labels?.startTimeValidationMessage ??
                              'cmp-date-range-start-validation-message',
                            'Pick a start date'
                          )
                  }
                }}
                name={name}
              />
            }
            endDateErrorMessage={
              <ErrorMessage
                errors={{
                  ...errors,
                  dates: {
                    ...errors.dates,
                    message:
                      errors.dates?.type === 'requiredDiffTime'
                        ? errors.dates?.message
                        : translate(
                            props.labels?.endDateValidationMessage ??
                              'cmp-date-range-end-validation-message',
                            'Pick an end date'
                          )
                  }
                }}
                name={name}
              />
            }
            endDateTimeErrorMessage={
              <ErrorMessage
                errors={{
                  ...errors,
                  dates: {
                    ...errors.dates,
                    message:
                      errors.dates?.type === 'requiredDiffTime'
                        ? errors.dates?.message
                        : translate(
                            props.labels?.endTimeValidationMessage ??
                              'cmp-date-range-end-validation-message',
                            'Pick an end date'
                          )
                  }
                }}
                name={name}
              />
            }
            startDateHasError={!!errors['dates'] && props.selectedDepartureDate === undefined}
            endDateHasError={
              props.singleDateSelection && props?.flightType === 'rt'
                ? !!errors['dates'] &&
                  props.selectedReturnDate === undefined &&
                  props.selectedDepartureDate !== undefined
                : !!errors['dates'] &&
                  props?.flightType === 'rt' &&
                  ((props.selectedReturnDate === undefined &&
                    props.selectedDepartureDate !== undefined) ||
                    (props.selectedReturnDate === undefined &&
                      props.selectedDepartureDate === undefined) ||
                    ((errors['dates'] as FieldError)?.type === 'requiredDiffTime' &&
                      !isValidDate(props.getValues?.().dates)))
            }
            defaultValue={props.defaultDates}
            timeOptions={timePickerOptions}
            showTimePickers={!!props.showTimePickers}
            singleSelection={props.singleDateSelection}
            minDate={props.blackoutDates?.minDate}
            maxDate={props.blackoutDates?.maxDate}
            maxRange={props.maxTripDuration}
            minRange={props.minTripDuration}
            dateFormat={datePicker?.dateFormat}
            dayFormat={datePicker?.dayFormat ?? 'D'}
            dateFormatOnEdit={datePicker?.dateFormatOnEdit}
            enableAutomaticStatusNavBarButtons={datePicker?.enableAutomaticStatusNavBarButtons}
            showFlexibleDates={flexibleDates?.enabled}
            flexibleDates={flexibleDates?.defaultValue}
            onChange={(e) => {
              props.updateSelectedDepartureDate(e.start);
              props.updateSelectedReturnDate(e.end);
              if (e.start !== undefined && e.start !== props.selectedDepartureDate) {
                props.onDepartureDateChange(e.start, e.end);
              } else if (e.end !== undefined && e.end !== props.selectedReturnDate) {
                props.onReturnDateChange(e.end, e.start);
              }
              onChange(e);
            }}
            onKeyDownHandleEnterAction={(e: any) => {
              if (e.keyCode === ENTER_KEY) {
                setTimeout(() => {
                  if (props?.trigger) {
                    props?.trigger('dates');
                  }
                  DateRangePickerRef?.current?.handleEnterAction();
                }, 200);
              }
            }}
            trigger={props?.trigger}
            fetchBlackoutDates={props?.fetchBlackoutDates}
            blackoutDatesIntegration={props?.blackoutDatesIntegration}
            selectedOrigin={selectedOrigin}
            selectedDestination={selectedDestination}
            flightType={props?.flightType}
            setValue={props?.setValue}
            getValues={props?.getValues}
            customInputStyle={datePicker?.customInputStyle}
            allowHoverStyleInRange={props.allowHoverStyleInRange}
          />
        )}
        name="dates"
        rules={{
          validate: {
            required: (value: Dates) => {
              if (props.singleDateSelection) {
                return (
                  !!props.selectedDepartureDate ||
                  translate(
                    props.labels?.startDateValidationMessage ??
                      'cmp-date-range-start-validation-message',
                    'Pick a start date'
                  )
                );
              } else {
                if (!!value?.start === false && !!value?.end === false) {
                  return translate(
                    props.labels?.rangeDateValidationMessage ?? 'cmp-date-range-validation-message',
                    'Pick a date range'
                  );
                } else if (!!value?.start === false && !!value?.end === true) {
                  return translate(
                    props.labels?.startDateValidationMessage ??
                      'cmp-date-range-start-validation-message',
                    'Pick a start date'
                  );
                } else if (!!value?.start === true && !!value?.end === false) {
                  return translate(
                    props.labels?.endDateValidationMessage ??
                      'cmp-date-range-end-validation-message',
                    'Pick an end date'
                  );
                } else {
                  return (
                    !!props.selectedDepartureDate ||
                    !!props.selectedReturnDate ||
                    translate(
                      props.labels?.rangeDateValidationMessage ??
                        'cmp-date-range-validation-message',
                      'Pick a date range'
                    )
                  );
                }
              }
            },
            requiredDiffTime: (value) => {
              if (
                !!props.showTimePickers &&
                !!value?.start === true &&
                !!value?.end === true &&
                !!value?.startTime === true &&
                !!value?.endTime === true
              ) {
                return (
                  isValidDate(value) ||
                  translate(
                    props.labels?.rangeDateTimeValidationMessage ??
                      'cmp-date-range-time-validation-message',
                    'Return time should be after departure time'
                  )
                );
              }
            },
            requiredStart: (value) =>
              !!props.selectedDepartureDate ||
              translate(
                props.labels?.startDateValidationMessage ??
                  'cmp-date-range-start-validation-message',
                'Pick a start date'
              ),
            requiredEnd: (value) => {
              return (
                props.singleDateSelection ||
                !!props.selectedReturnDate ||
                translate(
                  props.labels?.endDateValidationMessage ?? 'cmp-date-range-end-validation-message',
                  'Pick an end date'
                )
              );
            }
          }
        }}
        control={props.control}
      />
      {props.showAirlines ? (
        <Controller
          render={({ onChange }) => (
            <div>
              <Combobox
                label={translate('cmp-booking-filter-airlines-label', 'Airlines')}
                options={airlinesOptions}
                onChange={(newValue) => {
                  if (newValue.value && newValue.value !== selectedAirlines?.value) {
                    props.onAirlineChange(newValue);
                  }
                  onChange(newValue);
                }}
                defaultValue={props.prepopulatedAirlines ?? ''}
                errorMessage={<ErrorMessage errors={errors} name="airlines" />}
                hasError={
                  !!errors['airlines'] ||
                  (!disabledArLineFilter() &&
                    airlinesOptions.length === 0 &&
                    fetchAirlines.length !== 0)
                }
                placeholder={translate('cmp-booking-flight-airlines-placeholder', 'Choose Airline')}
                ariaLabelIconClose={translate('cmp-clear-icon', 'Clear')}
                ariaLabelIconToggle={translate('cmp-clear-icon-toggle', 'Toggle')}
                disabled={disabledArLineFilter()}
                {...airline}
              />
              {!disabledArLineFilter() &&
                airlinesOptions.length === 0 &&
                fetchAirlines.length !== 0 && (
                  <p style={{ fontSize: '12px' }}>
                    {translate(
                      'cmp-airlines-validation-message-no-airlines',
                      'No airlines available for the selected route'
                    )}
                  </p>
                )}
            </div>
          )}
          name="airlines"
          control={props.control}
          rules={{
            validate: {
              required: (airline) => {
                const newAirline = airline === undefined ? { label: '', value: '' } : airline;
                if (!disabledArLineFilter()) {
                  if (newAirline.label === '') {
                    return (
                      !!newAirline.label ||
                      translate('cmp-airlines-validation-message', 'Airlines is required')
                    );
                  }
                }
              }
            }
          }}
        />
      ) : null}
      <Tiles columns={props.ctaHalfWidth ? 2 : undefined} colGap={2} variant="layout.ctaContainer">
        {props.ctaHalfWidth ? <div /> : null}
        <Button
          type="submit"
          variant="primaryLg"
          disabled={
            !isNilOrEmpty(errors) &&
            (props.selectedDepartureDate === undefined ||
              props.selectedReturnDate === undefined ||
              props.getValues?.().destination?.value === '' ||
              props.getValues?.().origin?.value === '' ||
              !isValidDate(props.getValues?.().dates))
          }
          onClick={() => {
            if (props?.onSubmit) {
              props?.onSubmit();
            }
          }}
        >
          {props.isLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                variant: 'forms.spinner'
              }}
            >
              <Spinner size={32} />
            </Box>
          ) : (
            translate(labels.submit || 'Find Booking')
          )}
        </Button>
      </Tiles>

      {props.disclaimer?.enabled ? (
        props.disclaimer?.links ? (
          <Tiles variant="layout.disclaimerLinks" columns={[1, 3, 3, 5]} rowGap={2} colGap={2}>
            {props.disclaimer?.enabled ? (
              <Disclaimer label={'cmp-booking-mask-disclaimer'} {...props.disclaimer} />
            ) : null}
            {props.disclaimer?.links?.map((link) => (
              <DisclaimerLink {...link} />
            ))}
          </Tiles>
        ) : (
          <Disclaimer label={'cmp-booking-mask-disclaimer'} {...props.disclaimer} />
        )
      ) : null}
    </Columns>
  );
}
